import React, { Fragment, useEffect, useRef, useState } from "react";
import RGL, { WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { useWebRTC } from "../../../contexts/WebRTCContext";
import Badge from "../../../components/badges/Badge";
import { CameraBlock } from "./CameraBlock";

const GridLayout = WidthProvider(RGL);

export const CamerasGrid = () => {
  const { cams, layoutPreset } = useWebRTC();

  const GRID_COLS = 8;
  const GRID_ROWS = 4;
  const GRID_MARGIN = [16, 16];

  const gridContainerRef = useRef(null);
  const [gridHeight, setGridHeight] = useState(0);
  const [rowHeight, setRowHeight] = useState(120);
  const [layout, setLayout] = useState([]);
  const [fullscreenCamId, setFullscreenCamId] = useState(null);
  const [prevLayout, setPrevLayout] = useState(null)
  const lastLayoutPresetRef = useRef(null);

  useEffect(() => {
    const buildInitialLayout = (items) => {
      const list = items || [];
      const count = list.length;

      const isSmallDevice = window.innerWidth <= 900;

      if (count === 1) {
        if (isSmallDevice) {
          return [
            { i: list[0].id, x: 0, y: 0, w: 8, h: 4, static: false },
          ];
        }
        return [
          { i: list[0].id, x: 2, y: 0, w: 4, h: 4, static: false },
        ];
      }

      if (count === 2) {
        if (isSmallDevice) {
          return [
            { i: list[0].id, x: 0, y: 0, w: 8, h: 2, static: false },
            { i: list[1].id, x: 0, y: 2, w: 8, h: 2, static: false },
          ];
        }
        return [
          { i: list[0].id, x: 0, y: 0, w: 4, h: 4, static: false },
          { i: list[1].id, x: 4, y: 0, w: 4, h: 4, static: false },
        ];
      }

      if (count === 3) {
        const isValidPreset = Array.isArray(layoutPreset) && layoutPreset.length === 3;
        const orderedList = isValidPreset
          ? layoutPreset.map((idx) => list[idx]).filter(Boolean)
          : list;

        const a = orderedList[0] || list[0];
        const b = orderedList[1] || list[1];
        const c = orderedList[2] || list[2];

        if (isSmallDevice) {
          return [
            { i: a.id, x: 0, y: 0, w: 8, h: 2, static: false },
            { i: b.id, x: 0, y: 2, w: 4, h: 2, static: false },
            { i: c.id, x: 4, y: 2, w: 4, h: 2, static: false },
          ];
        }
        return [
          { i: a.id, x: 0, y: 0, w: 4, h: 4, static: false },
          { i: b.id, x: 4, y: 0, w: 4, h: 2, static: false },
          { i: c.id, x: 4, y: 2, w: 4, h: 2, static: false },
        ];
      }

      return list.map((cam, index) => ({
        i: cam.id,
        x: index % GRID_COLS,
        y: Math.floor(index / GRID_COLS),
        w: 1,
        h: 1,
        static: false,
      }));
    };

    if (fullscreenCamId) return;

    setLayout(buildInitialLayout(cams));
  }, [cams, layoutPreset, fullscreenCamId]);

  useEffect(() => {
    const CONTAINER_PADDING = GRID_MARGIN;
    const SAFETY_PX = 2;

    const updateGridMetrics = () => {
      if (!gridContainerRef.current) return;

      const rect = gridContainerRef.current.getBoundingClientRect();
      const nextGridHeight = rect.height;

      const paddingY = (CONTAINER_PADDING?.[1] ?? 0) * 2;
      const gapsY = (GRID_ROWS - 1) * (GRID_MARGIN?.[1] ?? 0);

      const usableHeight = Math.max(0, nextGridHeight - paddingY - gapsY - SAFETY_PX);
      const nextRowHeight = Math.max(1, Math.floor(usableHeight / GRID_ROWS));

      setGridHeight(nextGridHeight);
      setRowHeight(nextRowHeight);
    };

    updateGridMetrics();
    window.addEventListener("resize", updateGridMetrics);

    return () => {
      window.removeEventListener("resize", updateGridMetrics);
    };
  }, []);

  const handleLayoutChange = (newLayout) => {
    if (fullscreenCamId) return;
    setLayout(newLayout);
  };

  const toggleFullscreen = (camId) => {
    setFullscreenCamId((current) => {
      const isEntering = current !== camId;

      if (isEntering) {
        setPrevLayout(layout);

        const fullscreenLayout = (cams || []).map((cam) => {
          if (cam.id === camId) {
            return { i: camId, x: 0, y: 0, w: GRID_COLS, h: GRID_ROWS, static: false };
          }

          // Park other items outside the visible grid to avoid layout collisions
          return { i: cam.id, x: 0, y: GRID_ROWS, w: 0, h: 0, static: true };
        });

        setLayout(fullscreenLayout);
        return camId;
      }

      setLayout(prevLayout || layout);
      setPrevLayout(null);
      return null;
    });
  };

  useEffect(() => {
    if (layoutPreset === lastLayoutPresetRef.current) return;

    lastLayoutPresetRef.current = layoutPreset;

    if (fullscreenCamId) {
      setFullscreenCamId(null);
      setPrevLayout(null);
    }
  }, [layoutPreset, fullscreenCamId]);

  return (
    <div ref={gridContainerRef} className="h-full overflow-hidden">
      <GridLayout
        className="layout"
        layout={layout}
        cols={GRID_COLS}
        rowHeight={rowHeight}
        maxRows={GRID_ROWS}
        margin={GRID_MARGIN}
        containerPadding={GRID_MARGIN}
        isDraggable={!fullscreenCamId}
        isResizable={!fullscreenCamId}
        resizeHandles={["sw", "nw", "se", "ne"]}
        compactType={null}
        preventCollision={true}
        isBounded={true}
        autoSize={false}
        style={{ height: Math.floor(gridHeight) || "100%" }}
        onLayoutChange={handleLayoutChange}
        useCSSTransforms={true}
        draggableCancel=".no-drag"
      >
        {(cams || []).map((cam, camNumber) => {
          const isHidden = fullscreenCamId && cam.id !== fullscreenCamId;

          return (
            <div key={cam.id} style={{ display: isHidden ? "none" : "block" }}>
              <CameraBlock
                cam={cam}
                camNumber={camNumber + 1}
                isFullscreen={fullscreenCamId === cam.id}
                onToggleFullscreen={() => toggleFullscreen(cam.id)}
              />
            </div>
          );
        })}
      </GridLayout>
    </div>
  );
};
